import ApiResponse from "../models/apiResponse";
import AuthApi from "../api";
import { defineStore } from "pinia";
import ProfileProperties from "../models/profileProperties";
import User from "../models/user";
import Util from "../api/util";

const ACTIVE_WORKSPACE = "activeWorkspace";
const PROFILE = "profile";

export const useAuth = defineStore("auth", {
  state: () => {
    return {
      activeWorkspace: localStorage.getItem(ACTIVE_WORKSPACE),
      isLoading: false,
      errorMessage: null as string | null,
      profile: User.fromLocalStorageJSON(
        JSON.parse(localStorage.getItem(PROFILE) ?? "{}"),
      ) as User | null,
      token: null as string | null,
    };
  },
  getters: {
    avatarUrl(): string | undefined {
      return this.profile?.avatar?.url;
    },
    isAuthenticated(): boolean {
      return this.token !== null;
    },
    getProfile: (state) => {
      return () => state.profile;
    },
    hasActiveWorkspace(): boolean {
      return this.activeWorkspace !== null;
    },
    hasError(): boolean {
      return this.errorMessage !== null;
    },
  },
  actions: {
    initializeToken() {
      this.token = Util.getToken();
    },
    async fetchProfile() {
      try {
        let response = await AuthApi.fetchProfile();
        this.profile = response.data as User;
        localStorage.setItem(PROFILE, JSON.stringify(this.profile));
      } catch (error) {
        // Nothing yet.
      }
    },
    async login(email: string, password: string): Promise<void | Error> {
      this.isLoading = true;
      this.errorMessage = null;

      try {
        let response = await AuthApi.login(email, password);
        this.token = response.data.token;
        Util.saveToken(response.data.token);
        this.profile = response.data.user as User;
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      } finally {
        this.isLoading = false;
      }
    },
    selectActiveWorkspace(slug: string) {
      this.activeWorkspace = slug;
      localStorage.setItem(ACTIVE_WORKSPACE, slug);
    },
    async signup(
      name: string,
      email: string,
      password: string,
    ): Promise<void | Error> {
      this.isLoading = true;
      this.errorMessage = null;

      try {
        let response = await AuthApi.register(name, email, password);
        this.token = response.data.token;
        Util.saveToken(response.data.token);
        this.profile = response.data.user as User;
        this.selectActiveWorkspace(response.data.workspace.slug);
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      } finally {
        this.isLoading = false;
      }
    },
    async logout(): Promise<void> {
      try {
        await AuthApi.logout();
      } catch (error) {
        console.error(error);
      }

      this.clearCredentials();
      return Promise.resolve();
    },
    clearCredentials() {
      Util.removeToken();
      localStorage.removeItem(PROFILE);
      localStorage.removeItem(ACTIVE_WORKSPACE);
      this.token = null;
      this.profile = new User();
      this.activeWorkspace = null;
    },
    async requestPasswordReset(email: string) {
      this.isLoading = true;

      try {
        await AuthApi.requestPasswordReset(email);
      } catch (error) {
        // Nothing yet.
      } finally {
        this.isLoading = false;
      }
    },
    async resetPassword(token: string, password: string) {
      this.isLoading = true;

      try {
        await AuthApi.resetPassword(token, password);
      } catch (error) {
        // Nothing yet.
      } finally {
        this.isLoading = false;
      }
    },
    async updateProfile(properties: ProfileProperties) {
      this.isLoading = true;

      try {
        let response = await AuthApi.updateProfile(properties);
        this.profile = response.data.user as User;
      } catch (error) {
        // Nothing yet.
      } finally {
        this.isLoading = false;
      }
    },
    async updateProfileAvatar(file: File) {
      this.isLoading = true;

      try {
        let response = await AuthApi.updateProfileAvatar(file);
        this.profile = response.data.user as User;
      } catch (error) {
        // Nothing yet.
      } finally {
        this.isLoading = false;
      }
    },
  },
});
